// El menú que abre el `#` en la caja del Agente — ADR-2026-09-21-el-numeral-abre-colores y
// ADR-2026-09-21-pantone-en-el-menu-de-colores. Se apoya sobre la caja como el popover de
// Inteligencia (`absolute bottom-full`, sin portal: es chico). La lógica (qué se lista, en
// qué orden, el filtro) está en `coloresDelChat.ts`; acá sólo dibujo.
//
// Dos modos. En `colores`, el teclado lo maneja el textarea, que conserva el foco: por
// eso el menú apaga su `mousedown` (si no, el click primero desenfoca la caja y el menú se
// va antes de elegir), y "Buscar color Pantone…" es la última fila de la sección Pantone
// (índice `opciones.length` para las flechas). En `pantone`, el foco pasa al buscador que
// vive ADENTRO del menú: ahí se escribe, y sus flechas/Enter/Escape recorren, eligen y
// vuelven a los colores.

import { useEffect, useRef } from 'react';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import type { ModoDelMenu, OpcionDeColor, SeccionDeColor } from './coloresDelChat';

const ROTULO_DE_SECCION: Record<SeccionDeColor, string> = {
  recientes: 'Últimos usados',
  basicos: 'Colores básicos',
  pastel: 'Colores pastel',
  pantone: 'Colores Pantone',
  busqueda: 'Pantone · aproximaciones',
};

const FILA = 'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors';
const ROTULO_SECCION = 'px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.45)]';

/** El círculo del color; los muy claros llevan un aro para verse sobre blanco. */
export const Muestra: React.FC<{ hex: string; className?: string }> = ({ hex, className = 'h-5 w-5' }) => (
  <span
    aria-hidden="true"
    className={`inline-block shrink-0 rounded-full ring-1 ring-inset ring-[rgb(var(--brand-black-rgb)_/_0.12)] ${className}`}
    style={{ backgroundColor: hex }}
  />
);

/** Cuántas filas recorren las flechas: las opciones más, en modo `colores`, la de buscar Pantone. */
export const cantidadDeFilas = (opciones: readonly OpcionDeColor[], modo: ModoDelMenu): number =>
  opciones.length + (modo === 'colores' ? 1 : 0);

export const MenuDeColores: React.FC<{
  opciones: OpcionDeColor[];
  /** Índice de la fila resaltada (la que elige Enter); `opciones.length` es "Buscar color Pantone…". */
  activo: number;
  onActivar: (i: number) => void;
  onElegir: (opcion: OpcionDeColor) => void;
  /** "Nuevo color": abre el picker del navegador. */
  onNuevo: () => void;
  modo: ModoDelMenu;
  onCambiarModo: (m: ModoDelMenu) => void;
  /** Lo escrito en el buscador Pantone (modo `pantone`). */
  busqueda: string;
  onBusqueda: (texto: string) => void;
  /** El foco se fue del menú y de la caja: cerrar. */
  onSalir: () => void;
  raizRef: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ opciones, activo, onActivar, onElegir, onNuevo, modo, onCambiarModo, busqueda, onBusqueda, onSalir, raizRef }) => {
  const buscadorRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (modo === 'pantone') buscadorRef.current?.focus();
  }, [modo]);
  const sinMouseDown = (e: React.MouseEvent) => e.preventDefault();
  const hayPantoneVisible = opciones.some((o) => o.seccion === 'pantone');
  const filaDeBuscar = opciones.length;
  return (
    <div
      ref={raizRef}
      role="listbox"
      aria-label="Colores"
      className="absolute bottom-full left-3 z-[56] mb-2 w-[280px] rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.06)] bg-white p-1.5 shadow-xl animate-fade-in"
      onMouseDown={sinMouseDown}
    >
      {modo === 'pantone' && (
        <div className="flex items-center gap-1 pb-1">
          <button
            type="button"
            onClick={() => onCambiarModo('colores')}
            aria-label="Volver a los colores"
            title="Volver a los colores (Esc)"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[rgb(var(--brand-black-rgb)_/_0.55)] transition-colors hover:bg-[rgb(var(--brand-black-rgb)_/_0.05)] hover:text-brand-black"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#F4F6FB] px-3">
            <Search className="h-4 w-4 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.45)]" strokeWidth={2.25} aria-hidden="true" />
            <input
              ref={buscadorRef}
              value={busqueda}
              onChange={(e) => onBusqueda(e.target.value)}
              // El menú apaga el `mousedown` para que la caja no pierda el foco; acá SÍ
              // hace falta que el click enfoque el buscador.
              onMouseDown={(e) => e.stopPropagation()}
              onBlur={(e) => {
                const destino = e.relatedTarget as Node | null;
                if (destino && raizRef.current?.contains(destino)) return;
                onSalir();
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const n = opciones.length;
                  if (n > 0) onActivar(e.key === 'ArrowDown' ? (activo + 1) % n : (activo - 1 + n) % n);
                  return;
                }
                if (e.key === 'Enter' && opciones.length > 0) {
                  e.preventDefault();
                  onElegir(opciones[Math.min(activo, opciones.length - 1)]);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  onCambiarModo('colores');
                }
              }}
              placeholder="Código o nombre Pantone"
              aria-label="Buscar color Pantone"
              className="h-9 w-full bg-transparent text-[14px] text-brand-black outline-none placeholder:text-[rgb(var(--brand-black-rgb)_/_0.38)]"
            />
          </div>
        </div>
      )}
      <div className="max-h-[288px] overflow-y-auto">
        {opciones.length === 0 && (
          <p className="px-3 py-2.5 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
            {modo === 'pantone' ? 'Ningún Pantone con ese código o nombre.' : 'Ningún color con ese nombre.'}
          </p>
        )}
        {opciones.map((o, i) => {
          const primeraDeSeccion = i === 0 || opciones[i - 1].seccion !== o.seccion;
          return (
            <div key={o.hex}>
              {primeraDeSeccion && <p className={`${ROTULO_SECCION} ${i === 0 ? 'pt-1.5' : 'pt-2.5'}`}>{ROTULO_DE_SECCION[o.seccion]}</p>}
              <button
                type="button"
                role="option"
                aria-selected={i === activo}
                onMouseEnter={() => onActivar(i)}
                onClick={() => onElegir(o)}
                className={`${FILA} ${i === activo ? 'bg-[#F4F6FB]' : 'hover:bg-[#F4F6FB]'}`}
              >
                <Muestra hex={o.hex} />
                <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-brand-black">
                  {o.nombre}
                  {o.codigo && <span className="ml-1.5 text-[12px] font-normal text-[rgb(var(--brand-black-rgb)_/_0.5)]">{o.codigo}</span>}
                </span>
                {o.nombre !== o.hex && (
                  <span className="shrink-0 font-mono text-[11.5px] uppercase text-[rgb(var(--brand-black-rgb)_/_0.45)]">
                    {o.codigo ? '≈ ' : ''}
                    {o.hex}
                  </span>
                )}
              </button>
            </div>
          );
        })}
        {modo === 'colores' && (
          <div>
            {/* Si el filtro dejó afuera a todos los Pantone, la fila igual va con su rótulo. */}
            {!hayPantoneVisible && <p className={`${ROTULO_SECCION} ${opciones.length === 0 ? 'pt-1.5' : 'pt-2.5'}`}>{ROTULO_DE_SECCION.pantone}</p>}
            <button
              type="button"
              role="option"
              aria-selected={activo === filaDeBuscar}
              onMouseEnter={() => onActivar(filaDeBuscar)}
              onClick={() => onCambiarModo('pantone')}
              className={`${FILA} text-[14px] font-medium text-brand-black ${activo === filaDeBuscar ? 'bg-[#F4F6FB]' : 'hover:bg-[#F4F6FB]'}`}
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[rgb(var(--brand-black-rgb)_/_0.55)]">
                <Search className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              </span>
              Buscar color Pantone…
            </button>
          </div>
        )}
      </div>
      <div className="mt-1 border-t border-[rgb(var(--brand-black-rgb)_/_0.07)] pt-1">
        <button
          type="button"
          onClick={onNuevo}
          className={`${FILA} text-[14px] font-medium text-brand-primary hover:bg-[#F4F6FB]`}
        >
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed border-brand-primary/60">
            <Plus className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
          </span>
          Nuevo color…
        </button>
      </div>
    </div>
  );
};
